import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ReactComponent as EnterIconSVG } from '../../assets/images/enter-icon.svg';
import { ReactComponent as StatusErrorSVG } from '../../assets/images/status-error.svg';
import { ReactComponent as PlusIconSVG } from '../../assets/images/plus-icon.svg';
import { ReactComponent as CloseIconSVG } from '../../assets/images/close-icon.svg';
import { ReactComponent as StopIconSVG } from '../../assets/images/stop-icon.svg';
import { ReactComponent as ScreenshotIconSVG } from '../../assets/images/screenshot-icon.svg';
import { ReactComponent as AttachIconSVG } from '../../assets/images/attach-icon.svg';
import { nextTick } from "process";
import LoadingSpinner from "../LoadingSpinner";
import toast from "react-hot-toast";
import { Back4app2Error } from "@back4app2/sdk";
import type { AgentsSubscriptionInfo } from "@back4app2/sdk";
import back4app2 from "../../back4app2";
import {
  AGENT_ATTACHMENT_ALLOWED_TYPES,
  AGENT_ATTACHMENT_MAX_FILE_SIZE,
  AGENT_ATTACHMENT_MAX_FILES,
} from '../../types/Agent';

export interface UploadedFile {
  attachmentId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

// Pending file with upload state
export interface PendingFile {
  id: string; // unique id for tracking
  file: File;
  previewUrl: string;
  status: 'uploading' | 'uploaded' | 'failed';
  uploadedData?: UploadedFile;
}

const AgentInput = forwardRef<
  { setContent: (content: string) => void },
  {
    chatId: string;
    onSend: (content: string, uploadedFiles: UploadedFile[]) => Promise<boolean>; 
    onStop?: () => Promise<boolean>;
    onUpgrade?: () => void;
    onCreditsBannerVisibilityChange?: (visible: boolean) => void;
    disabled?: boolean;
    isResponding?: boolean;
    value?: string;
    onValueChange?: (value: string) => void;
    pendingFiles?: PendingFile[];
    onPendingFilesChange?: (
      files: PendingFile[] | ((prev: PendingFile[]) => PendingFile[])
    ) => void;
    showAttachments?: boolean;
  }
>(({ chatId, onSend, onStop, onUpgrade, onCreditsBannerVisibilityChange, disabled, isResponding, value, onValueChange, pendingFiles: pendingFilesProp, onPendingFilesChange, showAttachments = false }, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localContent, setLocalContent] = useState<string>('');
  const isControlled = value !== undefined;
  const content = isControlled ? value : localContent;
  const setContent = (nextValue: string) => {
    if (isControlled) {
      onValueChange?.(nextValue);
      return;
    }
    setLocalContent(nextValue);
  };
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isStopping, setIsStopping] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [localPendingFiles, setLocalPendingFiles] = useState<PendingFile[]>([]);
  const isPendingFilesControlled = pendingFilesProp !== undefined;
  const pendingFiles = isPendingFilesControlled ? pendingFilesProp : localPendingFiles;
  const setPendingFiles = useCallback((next: PendingFile[] | ((prev: PendingFile[]) => PendingFile[])) => {
    if (isPendingFilesControlled) {
      onPendingFilesChange?.(next);
      return;
    }
    setLocalPendingFiles(next);
  }, [isPendingFilesControlled, onPendingFilesChange]);
  const stoppingToastId = useRef<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Check if any file is currently uploading
  const isUploading = pendingFiles.some(f => f.status === 'uploading');
  const uploadedFiles = pendingFiles.filter(f => f.status === 'uploaded' && f.uploadedData).map(f => f.uploadedData!);
  // Block typing only when disabled, sending, or stopping - NOT when uploading
  const isInputBlocked = !!disabled || isSending || isStopping;
  // Block sending when uploading (must wait for files to finish)
  const isSendBlocked = isInputBlocked || isUploading;

  // Subscription usage (used to show FREE credits remaining today)
  const [subscription, setSubscription] = useState<AgentsSubscriptionInfo | null>(null);
  const [isCreditsBannerDismissed, setIsCreditsBannerDismissed] = useState(false);
  const [isUpgradeHovered, setIsUpgradeHovered] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const data = await back4app2.getMyAgentsSubscription();
      setSubscription(data);
    } catch {
      // If subscription can't be loaded, just hide the credits line
      setSubscription(null);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Reset isStopping when response finishes (isResponding becomes false)
  useEffect(() => {
    if (!isResponding && isStopping) {
      setIsStopping(false);
      // Dismiss the stopping toast when response finishes
      if (stoppingToastId.current) {
        toast.dismiss(stoppingToastId.current);
        stoppingToastId.current = null;
      }
    }
  }, [isResponding, isStopping]);

  // Handle click outside menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleStop = async () => {
    if (isStopping || !onStop) return;
    setIsStopping(true);
    
    // Show stopping toast
    stoppingToastId.current = toast.loading('Stopping generation...', {
      className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
    });
    
    try {
      const success = await onStop();
      
      if (!success) {
        // This shouldn't happen since API now throws on all failures
        // Keep as fallback just in case
        setIsStopping(false);
        if (stoppingToastId.current) {
          toast.dismiss(stoppingToastId.current);
          stoppingToastId.current = null;
        }
        return;
      }
      // Stop was sent successfully - wait for response to finish
    } catch (e) {
      // Only reset on error, otherwise wait for response to finish
      setIsStopping(false);
      if (stoppingToastId.current) {
        toast.dismiss(stoppingToastId.current);
        stoppingToastId.current = null;
      }
      
      // Show the error message from the API
      const errorMessage = e instanceof Back4app2Error 
        ? e.message 
        : 'Failed to stop generation';
      
      toast.error(errorMessage, {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);
  };

  useImperativeHandle(ref, () => ({
    setContent: (content: string) => {
      if (!isSending) {
        setContent(content);
        textareaRef.current?.focus();
      }
    }
  }));

  useEffect(
    () => {
      textareaRef.current?.focus();
    },
    []
  );

  const resizeTextarea = useCallback(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    const scrollHeight = textareaRef.current.scrollHeight;
    if (scrollHeight > 0) {
      textareaRef.current.style.height = scrollHeight + 'px';
    }
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [content, resizeTextarea]);

  useEffect(() => {
    const handleResize = () => resizeTextarea();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [resizeTextarea]);

  const getImageDimensions = (file: File): Promise<{ width: number; height: number } | null> =>
    new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      image.src = objectUrl;
    });

  // Upload a single file to S3 and update its state
  const uploadFile = useCallback(async (pendingFile: PendingFile) => {
    try {
      const dimensions = await getImageDimensions(pendingFile.file);
      if (!dimensions) {
        throw new Error('Failed to read image dimensions');
      }
      // Request presigned URL from backend
      const { attachmentId, presignedUrl } = await back4app2.requestPresignedUrl(
        chatId,
        pendingFile.file.name,
        pendingFile.file.type,
        pendingFile.file.size,
        dimensions.width,
        dimensions.height
      );

      // Upload file to S3
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': pendingFile.file.type,
        },
        body: pendingFile.file,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${pendingFile.file.name}`);
      }

      const uploadedData: UploadedFile = {
        attachmentId,
        fileName: pendingFile.file.name,
        mimeType: pendingFile.file.type,
        sizeBytes: pendingFile.file.size,
      };

      // Update state to mark as uploaded
      setPendingFiles(prev => prev.map(f => 
        f.id === pendingFile.id 
          ? { ...f, status: 'uploaded' as const, uploadedData } 
          : f
      ));
    } catch (err) {
      // Show error toast
      toast.error(err instanceof Error ? err.message : 'Failed to upload file', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });

      // Remove the failed file from state
      setPendingFiles(prev => {
        const file = prev.find(f => f.id === pendingFile.id);
        if (file) {
          URL.revokeObjectURL(file.previewUrl);
        }
        return prev.filter(f => f.id !== pendingFile.id);
      });
    }
  }, [chatId, setPendingFiles]);

  // Add files and start uploading immediately
  const addFilesAndUpload = useCallback((files: File[]) => {
    const newPendingFiles: PendingFile[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading' as const,
    }));

    setPendingFiles(prev => [...prev, ...newPendingFiles]);

    // Start uploading each file
    newPendingFiles.forEach(pf => uploadFile(pf));
  }, [setPendingFiles, uploadFile]);

  const send = async () => {
    if (isSendBlocked) return;
    if (content || uploadedFiles.length > 0) {
      const messageContent = content || '';
      const filesToSend = [...uploadedFiles];
      setIsSending(true);
      try {
        const success = await onSend(messageContent, filesToSend);
        if (!success) {
          return;
        }

        setContent('');
        pendingFiles.forEach(file => URL.revokeObjectURL(file.previewUrl));
        setPendingFiles([]);
        // Refresh usage so credits line updates after sending
        fetchSubscription();
      } catch (err) {
        console.error('[AgentInput] Error sending message:', err);
        toast.error(err instanceof Error ? err.message : 'Failed to send message', {
          className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 3000,
        });
      } finally {
        setIsSending(false);
        nextTick(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        });
      }
    } else if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (pendingFiles.length + selectedFiles.length > AGENT_ATTACHMENT_MAX_FILES) {
      toast.error(`Maximum ${AGENT_ATTACHMENT_MAX_FILES} files allowed`);
      return;
    }

    const validFiles = selectedFiles.filter(file => {
      if (!AGENT_ATTACHMENT_ALLOWED_TYPES.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}`);
        return false;
      }
      if (file.size > AGENT_ATTACHMENT_MAX_FILE_SIZE) {
        toast.error(`File too large: ${file.name}`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      addFilesAndUpload(validFiles);
    }
    setIsMenuOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleScreenshot = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: true,
        audio: false 
      });
      
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
            if (file.size > AGENT_ATTACHMENT_MAX_FILE_SIZE) {
              toast.error('Screenshot too large');
            } else if (pendingFiles.length >= AGENT_ATTACHMENT_MAX_FILES) {
              toast.error(`Maximum ${AGENT_ATTACHMENT_MAX_FILES} files allowed`);
            } else {
              addFilesAndUpload([file]);
            }
          }
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
        }, 'image/png');
      }
      setIsMenuOpen(false);
    } catch (err) {
      console.error('Error taking screenshot:', err);
    }
  };

  const removeFile = async (fileId: string) => {
    const file = pendingFiles.find(f => f.id === fileId);
    if (!file) return;

    // Remove from UI immediately
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
    URL.revokeObjectURL(file.previewUrl);

    // If the file was uploaded, delete it from S3/DB
    if (file.status === 'uploaded' && file.uploadedData?.attachmentId) {
      try {
        await back4app2.deleteAttachment(file.uploadedData.attachmentId);
      } catch (err) {
        // Log but don't show error to user - file is already removed from UI
        console.error('[AgentInput] Failed to delete attachment:', err);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Find any file item in clipboard
    let pastedFile: File | null = null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        pastedFile = item.getAsFile();
        break;
      }
    }

    // No file pasted, let default behavior handle text paste
    if (!pastedFile) return;

    // Prevent default since we're handling the file
    e.preventDefault();

    // Validate file type
    if (!AGENT_ATTACHMENT_ALLOWED_TYPES.includes(pastedFile.type)) {
      toast.error('Unsupported file type. Only PNG, JPEG, WebP, and GIF images are allowed.', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
      return;
    }

    // Validate file size
    if (pastedFile.size > AGENT_ATTACHMENT_MAX_FILE_SIZE) {
      toast.error('Image is too large (max 20MB)', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
      return;
    }

    // Validate max files limit
    if (pendingFiles.length >= AGENT_ATTACHMENT_MAX_FILES) {
      toast.error(`You can only attach up to ${AGENT_ATTACHMENT_MAX_FILES} files`, {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
      return;
    }

    addFilesAndUpload([pastedFile]);
  };

  const remainingFreeCredits =
    subscription?.planType === 'FREE'
      ? Math.max(subscription.chatMessagesHardLimit - subscription.chatMessagesUsage, 0)
      : null;

  const showFreeCreditsBanner = remainingFreeCredits !== null && !isCreditsBannerDismissed;

  const handleUpgradeClick = () => {
    if (onUpgrade) {
      onUpgrade();
    }
  };

  useEffect(() => {
    onCreditsBannerVisibilityChange?.(showFreeCreditsBanner);
  }, [showFreeCreditsBanner, onCreditsBannerVisibilityChange]);

  return (
    <div className="absolute w-full bottom-0 left-0 rounded-lg pb-0">
      <div className="mx-0 px-4 pb-4 pt-0">
        <div className="w-full relative">
          {/* Credits banner sits below, with only top radius, peeking from behind */}
          {showFreeCreditsBanner && (
            <div className="relative z-0 top-[12px] -mt-2 w-full glass-input-no-radius glass-input-opaque rounded-t-2xl rounded-b-none border-t-0 !bg-[#222832]">
              <div className="flex items-center justify-between gap-2 pr-2 pl-3 pt-[6px] pb-[16px]">
                <span className="text-xs text-white/70 truncate">
                  {remainingFreeCredits?.toLocaleString()} free messages remaining today
                </span>
                <div className="flex items-center gap-1.5 flex-none">
                  <button
                    type="button"
                    onClick={handleUpgradeClick}
                    onMouseEnter={() => setIsUpgradeHovered(true)}
                    onMouseLeave={() => setIsUpgradeHovered(false)}
                    className="px-2.5 py-1 rounded-md text-white text-xs font-semibold transition-colors"
                    style={{ backgroundColor: isUpgradeHovered ? '#19875F' : '#27AE60' }}
                  >
                    Upgrade Now
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreditsBannerDismissed(true)}
                    className="w-7 h-7 inline-flex items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                    aria-label="Dismiss"
                    title="Dismiss"
                  >
                    <CloseIconSVG width="10" height="10" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`relative z-10 w-full flex flex-col gap-2 glass-input glass-input-opaque px-4 py-3 focus-within:ring-2 focus-within:ring-[#208AEC]/60 ${isStopping ? 'pointer-events-none opacity-70' : ''}`}>
            {/* Row 1: File Previews + Textarea */}
            <div className="w-full pl-1">
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {pendingFiles.map((pendingFile) => (
                    <div key={pendingFile.id} className="relative group inline-block">
                      <div className="h-10 w-10 rounded-md border border-light-blue/20 overflow-hidden bg-black/20 flex items-center justify-center">
                        {pendingFile.status === 'uploading' ? (
                          <>
                            <img
                              src={pendingFile.previewUrl}
                              alt={pendingFile.file.name}
                              className="max-w-full max-h-full object-contain opacity-50"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <LoadingSpinner width="1rem" height="1rem" />
                            </div>
                          </>
                        ) : (
                          <img
                            src={pendingFile.previewUrl}
                            alt={pendingFile.file.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        )}
                      </div>
                      {pendingFile.status !== 'uploading' && (
                        <button
                          onClick={() => removeFile(pendingFile.id)}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#1a2332] border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
                          title="Remove image"
                        >
                          <CloseIconSVG width="8" height="8" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <textarea
                disabled={isInputBlocked}
                className={`w-full m-0 resize-none bg-transparent outline-none text-white placeholder:text-light-grey/70 leading-6 ${isStopping ? 'cursor-not-allowed' : ''}`}
                style={{ maxHeight: '120px' }}
                ref={textareaRef}
                placeholder={'Ask back4app...'}
                value={content}
                rows={1}
                onChange={handleChange}
                onPaste={handlePaste}
                onKeyDown={async (event) => {
                  if (event.key.toLowerCase() === 'enter') {
                    event.preventDefault();
                    if (isSendBlocked) return;
                    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
                      if (textareaRef.current) {
                        const startPos = textareaRef.current.selectionStart;
                        const endPos = textareaRef.current.selectionEnd;
                        const value = textareaRef.current.value;
                        const newValue = value.substring(0, startPos) + '\n' + value.substring(endPos);
                        setContent(newValue);
                        nextTick(() => {
                          if (textareaRef.current) {
                            textareaRef.current.setSelectionRange(startPos + 1, startPos + 1);
                            if (textareaRef.current.value.length === startPos + 1) {
                              textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
                            }
                          }
                        });
                      }
                    } else {
                      send();
                    }
                  }
                }}
              />
            </div>

            {/* Row 2: Buttons */}
            <div className="w-full flex justify-between items-center">
              {/* Plus Button with Menu */}
              <div className="relative">
                {showAttachments && (
                  <>
                    <button
                      ref={buttonRef}
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className={`flex-none inline-flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-200 ${
                        isMenuOpen
                          ? 'bg-[#34506F] border border-white/20'
                          : 'bg-white/10 text-light-blue hover:bg-white/15 border border-transparent'
                      }`}
                    >
                      <PlusIconSVG width="14" height="14" />
                    </button>

                    <div
                      ref={menuRef}
                      className={`absolute bottom-full left-0 mb-2 z-[9999] transition-all duration-200 ease-out origin-bottom-left ${
                        isMenuOpen
                          ? 'opacity-100 scale-100 translate-y-0'
                          : 'opacity-0 scale-95 translate-y-1 pointer-events-none'
                      }`}
                    >
                      <div className="w-48 bg-[#1a2332] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
                        <button
                          onClick={handleScreenshot}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                          type="button"
                        >
                          <ScreenshotIconSVG width="14" height="14" className="text-[#208AEC]" />
                          <span>Take screenshot</span>
                        </button>

                        <div className="h-px bg-white/5 mx-2" />

                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                          type="button"
                        >
                          <AttachIconSVG width="14" height="14" className="text-white/60" />
                          <span>Attach</span>
                        </button>
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept={AGENT_ATTACHMENT_ALLOWED_TYPES.join(',')}
                      multiple
                      onChange={handleFileSelect}
                    />
                  </>
                )}
              </div>

              <div className="relative">
                {isResponding && onStop ? (
                  <button
                    onClick={handleStop}
                    disabled={isStopping}
                    className={`flex-none inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                      isStopping
                        ? 'bg-system-yellow/20 text-system-yellow pointer-events-none opacity-50'
                        : 'bg-error-red/20 text-error-red hover:bg-error-red/30'
                    }`}
                    aria-label={isStopping ? "Stopping generation" : "Stop generation"}
                    title={isStopping ? "Stopping..." : "Stop generation"}
                  >
                    {isStopping ? (
                      <LoadingSpinner width='1.25rem' height='1.25rem' />
                    ) : (
                      <StopIconSVG width="20" height="20" />
                    )}
                  </button>
                ) : isSendBlocked ? (
                  <div className="w-10 h-10 flex items-center justify-center">
                    <LoadingSpinner width='2rem' height='2rem' />
                  </div>
                ) : (
                  <button
                    onClick={send}
                    disabled={(!content && uploadedFiles.length === 0)}
                    className={`flex-none inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                      (!content && uploadedFiles.length === 0)
                        ? 'bg-white/5 text-light-blue/30 cursor-not-allowed'
                        : 'bg-white/10 text-light-blue hover:bg-white/15'
                    }`}
                    aria-label="Send message"
                  >
                    <EnterIconSVG width="20" height="20" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AgentInput;
